import { Container, Graphics, Sprite, Text, Rectangle, type Texture } from 'pixi.js';
import { Scene } from '../app/SceneManager';
import { COLORS, DESIGN_W, DESIGN_H, BORDER, SCRIM } from '../app/theme';
import { viewport } from '../app/viewport';
import { Panel, Button, makeText, sceneBackground, avatarSprite, iconSprite, infoPill } from '../ui/widgets';
import { handFan, CARD_ASPECT } from '../ui/cardSprite';
import { BetControl } from '../ui/betControl';
import { roundResultOverlay } from '../ui/resultOverlay';
import { ChatControl } from '../ui/chatControl';
import { trayBackground, TRAY_TITLE } from '../ui/gameTray';
import { confettiBurst, loseFlash, floatingText, turnRing } from '../ui/effects';
import { scaleIn, shake, pulse, type TweenHandle } from '../app/anim';
import { blackjackSfxForState, initSound, playSfx, isSfxMuted, setSfxMuted } from '../app/sound';
import { loadTableTextures, type TableTextures } from '../assets/tableAssets';
import type { AppContext } from '../app/AppContext';
import type { Atlas } from '../assets/AtlasLoader';
import { MessageTag } from '../net/messageTags';
import { BlackjackActions, Role, RoomName } from '../net/enums';
import {
  type GameInfoSeat,
  decodeShowNextDealer,
  encodeExitView,
  encodeReadyToPlay,
  encodeResignGame,
} from '../net/codec/matchmaking';
import {
  decodeGetGame,
  decodeBlackjackPreview,
  decodeShowRoundResult,
  decodeShowEndGameResult,
  decodeDisconnect,
  encodeHit,
  encodeStand,
  encodeBet,
  encodeRefresh,
} from '../net/codec/blackjack';
import { decodeChat, encodeChat } from '../net/codec/chat';
import type { BlackjackPreviewPayload, GetGamePayload } from '../net/models/blackjackGame';
import { gameTimeLabel, moneyFa, toPersianDigits } from '../util/persian';
import { decodeInGameMessage } from '../net/codec/hokm';
import {
  BLACKJACK_ACTION_TRAY_H,
  BLACKJACK_HAND_MAX_WIDTH,
  BLACKJACK_INFO_PILL_Y,
  BLACKJACK_SCORE_PILL_Y,
  BLACKJACK_VALUE_CHIP_OFFSET,
  computeBlackjackLayout,
  type BlackjackLayout,
} from './blackjackLayout';
import { RESIGN_RESPONSE_TIMEOUT_MS, ResignLifecycle } from './resignLifecycle';

const WIN_GREEN = 0x4ade80;
const ORANGE = 0xd9722e;
const TOP_BAR_H = 112;

// Compact top-zone player pods share the header with the logo/menu, leaving more felt for cards.
const POD_W = 280;
const POD_H = 96;
const POD_Y = 8;
const POD_MARGIN = 8;
const POD_AVATAR = 64;
const POD_AV_X = 10;
const POD_AV_Y = 14;
const TURN_RING_R = POD_AVATAR / 2 + 7; // 39
const SCORE_PILL_Y = BLACKJACK_SCORE_PILL_Y;
const INFO_PILL_Y = BLACKJACK_INFO_PILL_Y;
// Avatar centers (board coords) — the active-turn ring + countdown node sit on these.
const AV_CY = POD_Y + POD_AV_Y + POD_AVATAR / 2; // 54
const ME_AV_CX = POD_MARGIN + POD_AV_X + POD_AVATAR / 2; // 50
const OPP_AV_CX = DESIGN_W - POD_MARGIN - POD_W + (POD_W - POD_AV_X - POD_AVATAR) + POD_AVATAR / 2; // 670

const ACTION_TRAY = { x: 12, w: DESIGN_W - 24, h: BLACKJACK_ACTION_TRAY_H };

/**
 * The Blackjack table — re-skinned to the Unity game (green felt, top bar with the Blackjack emblem,
 * banker/min-bet, both-players strip, center bet box, slide-in action/raise/result trays, quick-chat
 * bubbles and game-engine juice). The server stays the single source of truth: this scene only
 * renders the latest GetGame and sends Hit/Stand/Bet/Chat/Resign. Layout is role-relative.
 */
export class BlackjackTableScene extends Scene {
  private cards: Atlas | null = null;
  private table: TableTextures = {};
  private game: GetGamePayload | null = null;
  private awaitingState = false;
  private chatEnabled = true;
  private readonly resignFlow = new ResignLifecycle();

  /** Spectators use Player1 as their lower/left visual perspective. */
  private readonly viewRole: Role;
  private readonly isPreview: boolean;
  private readonly bgLayer = new Container(); // full-viewport wood backdrop (rebuilt on resize)
  private readonly feltLayer = new Container();
  private readonly chrome = new Container(); // top bar, logo, menu, right rail (static)
  private readonly board = new Container(); // rebuilt every render()
  private readonly fx = new Container(); // effects + chat bubbles
  private readonly overlay = new Container(); // trays / menu / banners
  private chat!: ChatControl;

  // Live layout, recomputed from viewport.h each render.
  private ly: BlackjackLayout = computeBlackjackLayout(DESIGN_H);

  // Animation bookkeeping.
  private prevMyLen = 0;
  private prevOppLen = 0;
  private prevValue = -1;
  private timerHandle: TweenHandle | null = null;

  constructor(
    private readonly ctx: AppContext,
    private readonly seat: GameInfoSeat,
  ) {
    super();
    this.isPreview = seat.isPreview;
    this.viewRole = seat.isPreview ? Role.Player1 : seat.myRole;
  }

  onEnter(): void {
    this.addChild(this.bgLayer, this.feltLayer, this.chrome, this.board, this.fx, this.overlay);
    this.rebuildBg();

    this.chat = new ChatControl({
      respackFrame: (n) => this.ctx.respackFrame(n),
      onSend: (text) => this.sendChat(text),
      mySide: { x: 150, y: 96 },
      oppSide: { x: DESIGN_W - 150, y: 96 },
    });
    this.fx.addChild(this.chat);

    initSound(); // decode the table SFX (card/win/lose/bet); unlocks on first gesture

    this.buildChrome();

    // Subscribe synchronously (onEnter is not awaited; the previous scene is destroyed right after).
    this.track(this.ctx.client.on(MessageTag.GetGame, (r) => this.onGetGame(decodeGetGame(r))));
    this.track(this.ctx.client.on(MessageTag.GamePreview, (r) => this.onPreview(decodeBlackjackPreview(r))));
    this.track(this.ctx.client.on(MessageTag.ShowNextDealer, (r) => this.onNextDealer(decodeShowNextDealer(r))));
    this.track(this.ctx.client.on(MessageTag.ShowRoundResult, (r) => this.onRoundResult(decodeShowRoundResult(r))));
    this.track(this.ctx.client.on(MessageTag.InGameMessage, (r) => this.onInGameMessage(decodeInGameMessage(r))));
    this.track(this.ctx.client.on(MessageTag.ShowEndGameResult, (r) => this.onEndGame(decodeShowEndGameResult(r))));
    this.track(this.ctx.client.on(MessageTag.Disconnect, (r) => this.onDisconnect(decodeDisconnect(r))));
    this.track(this.ctx.client.on(MessageTag.Chat, (r) => this.onChat(decodeChat(r))));

    // Lazily load the card atlas + table art; re-render / build the felt when each is ready.
    void import('../assets/cardAtlas')
      .then(({ loadCardAtlas }) => loadCardAtlas())
      .then((atlas) => {
        this.cards = atlas;
        this.render();
      })
      .catch((err: unknown) => console.warn('[blackjack] card atlas failed to load', err));

    void loadTableTextures()
      .then((tex) => {
        this.table = tex;
        this.buildFelt();
        this.buildChrome();
        this.render();
      })
      .catch((err: unknown) => console.warn('[blackjack] table art failed to load', err));

    if (!this.isPreview) this.ctx.client.send(encodeReadyToPlay());
    this.render();
  }

  /** Re-flow on viewport-height change (Telegram fullscreen / rotation): rebuild bg, felt, chrome, board. */
  onResize(): void {
    this.rebuildBg();
    this.buildFelt();
    this.buildChrome();
    this.render();
  }

  /** Rebuild the full-viewport wood backdrop (fills the live height). */
  private rebuildBg(): void {
    for (const ch of this.bgLayer.removeChildren()) ch.destroy({ children: true });
    this.bgLayer.addChild(sceneBackground());
  }

  private computeLayout(): void {
    this.ly = computeBlackjackLayout(viewport.h);
  }

  // ---- static chrome ----

  private buildFelt(): void {
    // The felt art (public/img/table.png) is the only place the table's central emblem lives — it is
    // regenerated as a clean Blackjack felt (no HOKM). The scene must not add any HOKM branding.
    this.feltLayer.removeChildren().forEach((c) => c.destroy());
    const tex = this.table.table;
    if (!tex) return;
    const s = new Sprite(tex);
    s.anchor.set(0.5, 0);
    // Cover-fit the felt from below the top bar to the live bottom, so the table fills tall screens
    // (width-fit alone left a wood gap below the felt). A slight side-crop is fine for a felt texture.
    s.scale.set(Math.max(DESIGN_W / tex.width, (viewport.h - TOP_BAR_H) / tex.height));
    s.position.set(DESIGN_W / 2, TOP_BAR_H);
    this.feltLayer.addChild(s);
  }

  private buildChrome(): void {
    this.computeLayout();
    for (const c of this.chrome.removeChildren()) c.destroy({ children: true });

    // Top bar.
    const bar = new Graphics().rect(0, 0, DESIGN_W, TOP_BAR_H).fill({ color: 0x0a0a0c, alpha: 0.92 });
    this.chrome.addChild(bar);

    const logoTex = this.ctx.iconTexture('blackjack');
    if (logoTex) {
      const logo = iconSprite(logoTex, 48);
      logo.position.set(DESIGN_W / 2 - 24, 5);
      this.chrome.addChild(logo);
    }

    const menu = new Button(this.isPreview ? 'بازگشت' : 'منوی بازی', () => this.menuAction(), {
      width: 120,
      height: 38,
      fontSize: 18,
      icon: this.isPreview ? (this.ctx.iconTexture('return') ?? null) : null,
    });
    menu.position.set(DESIGN_W / 2 - 60, 66);
    this.chrome.addChild(menu);

    if (this.isPreview) return;

    // Right rail (mid-felt, clear of the action tray): refresh (resync) + گپ (chat).
    const refresh = this.railButton('', () => this.ctx.client.send(encodeRefresh()), this.ctx.iconTexture('refresh-page-option'));
    refresh.position.set(DESIGN_W - 76, this.ly.railRefreshY);
    this.chrome.addChild(refresh);
    const chatBtn = this.railButton('گپ', () => this.toggleChat(), this.ctx.respackFrame('chat'));
    chatBtn.position.set(DESIGN_W - 76, this.ly.railChatY);
    this.chrome.addChild(chatBtn);
  }

  /** A small orange right-rail button (icon and/or label). */
  private railButton(label: string, onTap: () => void, tex?: Texture): Container {
    const c = new Container();
    const w = 64;
    const h = 64;
    const g = new Graphics().roundRect(0, 0, w, h, 12).fill({ color: ORANGE });
    c.addChild(g);
    if (tex) {
      const icon = iconSprite(tex, label ? 34 : 44);
      icon.position.set((w - (label ? 34 : 44)) / 2, label ? 6 : 10);
      c.addChild(icon);
    }
    if (label) {
      const t = makeText(label, { size: 20, weight: '700', align: 'center', fill: COLORS.white });
      t.anchor.set(0.5);
      t.position.set(w / 2, tex ? h - 16 : h / 2);
      c.addChild(t);
    }
    c.hitArea = new Rectangle(0, 0, w, h);
    c.eventMode = 'static';
    c.cursor = 'pointer';
    c.on('pointertap', onTap);
    c.on('pointerover', () => (c.alpha = 0.85));
    c.on('pointerout', () => (c.alpha = 1));
    return c;
  }

  // ---- incoming messages ----

  private onGetGame(game: GetGamePayload, clearOverlay = true): void {
    if (this.resignFlow.isTerminal) return;
    playSfx(blackjackSfxForState(this.game, game));
    this.game = game;
    this.awaitingState = false;
    if (clearOverlay) this.clearOverlay();
    this.render();
  }

  /** Convert the role-oriented spectator snapshot into the scene's lower(P1)/upper(P2) view. */
  private onPreview(preview: BlackjackPreviewPayload): void {
    const handFor = (role: Role): string[] => {
      if (preview.dealerRole === role) return preview.dealerHand;
      if (preview.playerRole === role) return preview.playerHand;
      return [];
    };
    const game: GetGamePayload = {
      myHand: handFor(Role.Player1),
      oppHand: handFor(Role.Player2),
      minBet: preview.minBet,
      bet: preview.bet,
      player1Balance: preview.player1Balance,
      player2Balance: preview.player2Balance,
      dealerRole: preview.dealerRole,
      playerRole: preview.playerRole,
      actions: [],
      turn: preview.turn,
      turnNextTrigger: preview.turnNextTrigger,
      player1TimeBank: preview.player1TimeBank,
      player2TimeBank: preview.player2TimeBank,
      player1TotalWin: preview.player1TotalWin,
      player2TotalWin: preview.player2TotalWin,
      myHandValue: 0,
    };

    // FinishRoundAsync broadcasts ShowRoundResult, then immediately builds both players' delayed
    // GetGame packets. Each build also emits an *undelayed* GamePreview, so clearing overlays here
    // made the spectator result disappear in the same network tick. Keep the overlay alive while the
    // board advances underneath it; its close button / fallback timer dismisses it normally.
    this.onGetGame(game, false);
  }

  private onNextDealer(nd: ReturnType<typeof decodeShowNextDealer>): void {
    if (this.resignFlow.isTerminal) return;
    const name = nd.dealerRole === Role.Player1 ? this.seat.player1.username : this.seat.player2.username;
    this.banner(`دست جدید — بانکدار: ${name}`, COLORS.gold);
  }

  private onRoundResult(rr: ReturnType<typeof decodeShowRoundResult>): void {
    if (this.resignFlow.isTerminal) return;
    this.resignFlow.acknowledge();
    const iAmDealer = this.viewRole === (this.game?.dealerRole ?? Role.None);
    const myWon = rr.winner === this.viewRole;
    const view = {
      atlas: this.cards,
      myWon,
      myHand: iAmDealer ? rr.dealerHand : rr.playerHand,
      myValue: iAmDealer ? rr.dealerValue : rr.playerValue,
      oppHand: iAmDealer ? rr.playerHand : rr.dealerHand,
      oppValue: iAmDealer ? rr.playerValue : rr.dealerValue,
      reason: rr.reason,
      title: this.isPreview ? 'نتیجه دست' : undefined,
      myLabel: this.isPreview ? this.seat.player1.username : undefined,
      oppLabel: this.isPreview ? this.seat.player2.username : undefined,
      onClose: () => this.clearOverlay(),
    };
    this.clearOverlay();
    const ov = roundResultOverlay(view);
    this.overlay.addChild(ov);
    playSfx(myWon ? 'blackjack-win' : 'blackjack-lose');
    // Celebrate / commiserate.
    if (!this.isPreview) {
      if (myWon) confettiBurst(this.fx);
      else loseFlash(this.fx);
    }
    // The next round's GetGame also clears it; this is the fallback timeout.
    this.after(5000, () => {
      if (!ov.destroyed) ov.destroy({ children: true });
    });
  }

  private onEndGame(eg: ReturnType<typeof decodeShowEndGameResult>): void {
    if (!this.resignFlow.complete()) return;
    this.showEndGame(eg.resultText, eg.reasonText);
  }

  private onInGameMessage(text: string): void {
    if (this.resignFlow.isTerminal) return;
    if (this.resignFlow.acknowledge()) this.clearOverlay();
    if (text) this.banner(text, COLORS.gold);
    this.awaitingState = false;
    this.render();
  }

  private onDisconnect(d: ReturnType<typeof decodeDisconnect>): void {
    if (this.resignFlow.isTerminal) return;
    this.banner(d.isLeaving ? 'حریف از بازی خارج شد' : 'حریف به بازی بازگشت', d.isLeaving ? COLORS.danger : COLORS.gold);
  }

  private onChat(payload: ReturnType<typeof decodeChat>): void {
    if (this.resignFlow.isTerminal || !this.chatEnabled) return;
    const side = payload.senderRole === this.viewRole ? 'me' : 'opp';
    this.chat.showBubble(side, payload.text);
  }

  // ---- actions ----

  private send(bytes: Uint8Array): void {
    this.ctx.client.send(bytes);
    this.awaitingState = true; // disable the action bar until the next GetGame
    this.render();
  }

  private sendChat(text: string): void {
    this.ctx.client.send(encodeChat(text));
    this.chat.showBubble('me', text); // the server does not echo our own chat back
  }

  private toggleChat(): void {
    if (this.chatEnabled) this.chat.toggle();
    else this.banner('چت غیرفعال است', COLORS.textDim);
  }

  /** Spectators leave the watch list; seated players open the regular game menu. */
  private menuAction(): void {
    if (!this.isPreview) {
      this.openMenu();
      return;
    }
    this.ctx.client.send(encodeExitView());
    this.ctx.navigate(RoomName.MainMenu);
  }

  private openBet(g: GetGamePayload): void {
    const myIsP1 = this.viewRole === Role.Player1;
    const myBalance = myIsP1 ? g.player1Balance : g.player2Balance;
    const oppBalance = myIsP1 ? g.player2Balance : g.player1Balance;
    const maxRaw = myBalance < oppBalance ? myBalance : oppBalance;
    const max = Math.min(Number(maxRaw), 2_147_483_647);
    this.clearOverlay();
    this.overlay.addChild(
      new BetControl({
        current: g.bet,
        step: g.minBet,
        max,
        // A successful Bet auto-deals a card server-side — do NOT also send Hit.
        onConfirm: (amount) => {
          floatingText(this.fx, `+${moneyFa(Math.max(0, amount - g.bet))}`, DESIGN_W / 2, this.ly.betBoxY - 50);
          this.send(encodeBet(amount));
        },
        onCancel: () => this.render(),
      }),
    );
  }

  private openMenu(): void {
    this.clearOverlay();
    const layer = new Container();
    const dim = new Graphics().rect(0, 0, DESIGN_W, viewport.h).fill({ color: COLORS.scrim, alpha: 0.55 });
    dim.eventMode = 'static';
    dim.on('pointertap', () => this.clearOverlay());
    layer.addChild(dim);

    const boxW = 520;
    const boxH = 330;
    const panel = new Panel(boxW, boxH);
    panel.position.set((DESIGN_W - boxW) / 2, (viewport.h - boxH) / 2);
    layer.addChild(panel);

    const title = makeText('منوی بازی', { size: 30, weight: '700', align: 'center', fill: COLORS.gold });
    title.anchor.set(0.5, 0);
    title.position.set(boxW / 2, 26);
    panel.addChild(title);

    const resign = new Button('اعلام باخت', () => this.confirmResign(), { width: boxW - 80, height: 64, fill: COLORS.danger });
    resign.position.set(40, 84);
    panel.addChild(resign);

    // Chat + sound toggles, side by side.
    const halfW = (boxW - 80 - 20) / 2;
    const chatToggle = new Button(this.chatEnabled ? 'خاموش کردن چت' : 'روشن کردن چت', () => {
      this.chatEnabled = !this.chatEnabled;
      this.clearOverlay();
    }, { width: halfW, height: 64, fontSize: 20, fill: COLORS.panelSoft, textColor: COLORS.text });
    chatToggle.position.set(40, 164);
    panel.addChild(chatToggle);

    const soundToggle = new Button(isSfxMuted() ? 'روشن کردن صدا' : 'خاموش کردن صدا', () => {
      setSfxMuted(!isSfxMuted());
      this.clearOverlay();
    }, { width: halfW, height: 64, fontSize: 20, fill: COLORS.panelSoft, textColor: COLORS.text });
    soundToggle.position.set(40 + halfW + 20, 164);
    panel.addChild(soundToggle);

    const close = new Button('بستن', () => this.clearOverlay(), { width: boxW - 80, height: 64 });
    close.position.set(40, 244);
    panel.addChild(close);

    this.overlay.addChild(layer);
    scaleIn(panel, 220, 0.85);
  }

  /** Confirm before forfeiting (Resign → ShowResignDialog). Tapping outside / "بازگشت" returns to the menu. */
  private confirmResign(): void {
    this.confirmDialog(
      'اعلام باخت',
      'با اعلام باخت، این بازی را واگذار می‌کنید و حریف برنده می‌شود.',
      'بله، باخت را می‌پذیرم',
      () => this.resignGame(),
    );
  }

  /** Shared danger-confirm dialog (a scrim + Panel in the overlay). "بازگشت"/scrim-tap returns to the menu. */
  private confirmDialog(titleText: string, message: string, confirmLabel: string, onConfirm: () => void): void {
    this.clearOverlay();
    const layer = new Container();
    const dim = new Graphics().rect(0, 0, DESIGN_W, viewport.h).fill({ color: COLORS.scrim, alpha: SCRIM.dialog });
    dim.eventMode = 'static';
    dim.on('pointertap', () => this.openMenu());
    layer.addChild(dim);

    const boxW = 560;
    const boxH = 320;
    const panel = new Panel(boxW, boxH);
    panel.position.set((DESIGN_W - boxW) / 2, (viewport.h - boxH) / 2);
    panel.eventMode = 'static';
    panel.hitArea = new Rectangle(0, 0, boxW, boxH);
    layer.addChild(panel);

    const title = makeText(titleText, { size: 30, weight: '700', align: 'center', fill: COLORS.danger });
    title.anchor.set(0.5, 0);
    title.position.set(boxW / 2, 30);
    panel.addChild(title);

    const msg = makeText(message, { size: 22, align: 'center', fill: COLORS.text });
    msg.anchor.set(0.5, 0);
    msg.style.wordWrap = true;
    msg.style.wordWrapWidth = boxW - 80;
    msg.position.set(boxW / 2, 102);
    panel.addChild(msg);

    const btnW = (boxW - 100) / 2;
    const yes = new Button(confirmLabel, onConfirm, { width: btnW, height: 64, fontSize: 20, fill: COLORS.danger });
    yes.position.set(40, boxH - 92);
    panel.addChild(yes);
    const no = new Button('بازگشت', () => this.openMenu(), { width: btnW, height: 64, fill: COLORS.panelSoft, textColor: COLORS.text });
    no.position.set(40 + btnW + 20, boxH - 92);
    panel.addChild(no);

    this.overlay.addChild(layer);
    scaleIn(panel, 220, 0.85);
  }

  private showEndGame(resultText: string, reasonText: string): void {
    this.clearOverlay();
    const layer = new Container();
    const dim = new Graphics().rect(0, 0, DESIGN_W, viewport.h).fill({ color: COLORS.scrim, alpha: 0.7 });
    dim.eventMode = 'static';
    layer.addChild(dim);

    const boxW = 600;
    const boxH = 420;
    const panel = new Panel(boxW, boxH);
    panel.position.set((DESIGN_W - boxW) / 2, (viewport.h - boxH) / 2);
    layer.addChild(panel);

    const title = makeText('پایان بازی', { size: 32, weight: '700', align: 'center', fill: COLORS.gold });
    title.anchor.set(0.5, 0);
    title.position.set(boxW / 2, 28);
    panel.addChild(title);

    const result = makeText(resultText, { size: 26, weight: '700', align: 'center', fill: COLORS.white });
    result.style.wordWrap = true;
    result.style.wordWrapWidth = boxW - 64;
    result.anchor.set(0.5, 0);
    result.position.set(boxW / 2, 110);
    panel.addChild(result);

    if (reasonText) {
      const reason = makeText(reasonText, { size: 22, align: 'center', fill: COLORS.textDim });
      reason.style.wordWrap = true;
      reason.style.wordWrapWidth = boxW - 64;
      reason.anchor.set(0.5, 0);
      reason.position.set(boxW / 2, 200);
      panel.addChild(reason);
    }

    const back = new Button('بازگشت به منو اصلی', () => this.ctx.navigate(RoomName.MainMenu), {
      width: boxW - 80,
      height: 72,
      fontSize: 26,
    });
    back.position.set(40, boxH - 96);
    panel.addChild(back);

    this.overlay.addChild(layer);
    scaleIn(panel, 260, 0.8);
  }

  /** Ask to forfeit; the server decides whether this ends a hand or the whole match. */
  private resignGame(): void {
    if (!this.resignFlow.begin()) return;

    this.awaitingState = true;
    this.clearOverlay();
    this.banner('در انتظار پاسخ سرور…', COLORS.gold, RESIGN_RESPONSE_TIMEOUT_MS);
    this.render();
    try {
      this.ctx.client.send(encodeResignGame());
    } catch {
      this.resignFlow.acknowledge();
      this.awaitingState = false;
      this.clearOverlay();
      this.banner('درخواست ارسال نشد؛ اتصال به سرور در دسترس نیست.', COLORS.danger);
      this.render();
      return;
    }

    this.after(RESIGN_RESPONSE_TIMEOUT_MS, () => {
      if (!this.resignFlow.acknowledge()) return;
      this.awaitingState = false;
      this.clearOverlay();
      this.banner('پاسخی از سرور دریافت نشد؛ دوباره تلاش کنید.', COLORS.danger);
      this.render();
    });
  }

  // ---- rendering ----

  private render(): void {
    this.computeLayout();
    this.timerHandle?.cancel();
    this.timerHandle = null;
    for (const child of this.board.removeChildren()) child.destroy({ children: true });

    const g = this.game;
    if (!g) {
      this.board.addChild(this.centered('در انتظار شروع بازی…', viewport.h / 2, { size: 26, fill: COLORS.gold }));
      return;
    }

    const myIsP1 = this.viewRole === Role.Player1;
    const myBalance = myIsP1 ? g.player1Balance : g.player2Balance;
    const oppBalance = myIsP1 ? g.player2Balance : g.player1Balance;
    const myWins = myIsP1 ? g.player1TotalWin : g.player2TotalWin;
    const oppWins = myIsP1 ? g.player2TotalWin : g.player1TotalWin;
    const myAvatar = myIsP1 ? this.seat.player1.avatar : this.seat.player2.avatar;
    const oppAvatar = myIsP1 ? this.seat.player2.avatar : this.seat.player1.avatar;
    const myName = myIsP1 ? this.seat.player1.username : this.seat.player2.username;
    const oppName = myIsP1 ? this.seat.player2.username : this.seat.player1.username;
    const dealerName = g.dealerRole === Role.Player1 ? this.seat.player1.username : this.seat.player2.username;
    const myTurn = g.turn === this.viewRole;
    const myBank = myIsP1 ? g.player1TimeBank : g.player2TimeBank;
    const oppBank = myIsP1 ? g.player2TimeBank : g.player1TimeBank;

    // Player pods (avatar + name + balance + time-bank), me top-left, opponent top-right.
    this.board.addChild(this.playerPod('me', { avatar: myAvatar, name: myName, balance: myBalance, timeBank: myBank }));
    this.board.addChild(this.playerPod('opp', { avatar: oppAvatar, name: oppName, balance: oppBalance, timeBank: oppBank }));

    // Centered info pills: score, then banker · min-bet · game-time.
    const score = infoPill(`${toPersianDigits(myWins)} : امتیاز : ${toPersianDigits(oppWins)}`, {
      height: 42, fontSize: 20, glow: false, textColor: COLORS.gold,
    });
    score.position.set((DESIGN_W - score.pillWidth) / 2, SCORE_PILL_Y);
    this.board.addChild(score);
    const info = infoPill(
      `بانکدار: ${dealerName}  ·  کمترین: ${moneyFa(g.minBet)}  ·  ${gameTimeLabel(this.seat.game.gameTime)}`,
      { width: 580, height: 40, fontSize: 18, glow: false, textColor: COLORS.textDim },
    );
    info.position.set((DESIGN_W - 580) / 2, INFO_PILL_Y);
    this.board.addChild(info);

    // Hands (deal animation for newly appended cards).
    this.handLayer(g.oppHand, this.ly.oppHandY, this.prevOppLen, this.ly.oppCardW);
    this.handLayer(g.myHand, this.ly.myHandY, this.prevMyLen, this.ly.cardW);
    this.prevOppLen = g.oppHand.length;
    this.prevMyLen = g.myHand.length;

    // Bet box + seated player's hand value (the preview wire format intentionally omits the value).
    this.board.addChild(this.betBox(g.bet));
    if (!this.isPreview) {
      this.board.addChild(this.valueChip(g.myHandValue));
      this.prevValue = g.myHandValue;
    }

    // Active-turn timer ring + countdown node on the active player's avatar (green turn time → red reserve).
    this.timerHandle = this.turnTimer(myTurn ? ME_AV_CX : OPP_AV_CX, g.turnNextTrigger, myTurn ? myBank : oppBank);

    // Action tray.
    if (this.isPreview) {
      this.board.addChild(this.centered('در حال تماشای بازی', this.ly.trayY + ACTION_TRAY.h / 2, { size: 24, fill: COLORS.gold }));
    } else if (myTurn && !this.awaitingState) {
      this.board.addChild(this.actionTray(g));
    } else {
      this.board.addChild(this.centered(this.awaitingState ? 'در حال پردازش…' : 'نوبت حریف…', this.ly.trayY + ACTION_TRAY.h / 2, { size: 24, fill: COLORS.textDim }));
    }
  }

  /** One player's pod: a wood plate with the avatar (outer edge), name, balance, and a time-bank chip. */
  private playerPod(side: 'me' | 'opp', d: { avatar: number; name: string; balance: bigint; timeBank: number }): Container {
    const pod = new Panel(POD_W, POD_H, { fill: COLORS.panel, alpha: 0.92, shadow: true });
    pod.position.set(side === 'me' ? POD_MARGIN : DESIGN_W - POD_MARGIN - POD_W, POD_Y);

    const avX = side === 'me' ? POD_AV_X : POD_W - POD_AV_X - POD_AVATAR;
    const av = avatarSprite(this.ctx.profileAtlas, d.avatar, POD_AVATAR);
    av.position.set(avX, POD_AV_Y);
    pod.addChild(av);

    if (side === 'me') {
      const tx = POD_AV_X + POD_AVATAR + 12;
      pod.addChild(this.leftText(d.name, tx, 10, 20, COLORS.text, '700'));
      pod.addChild(this.leftText(moneyFa(d.balance), tx, 36, 16, COLORS.gold));
      pod.addChild(this.timeChip(d.timeBank, tx, 58, 'me'));
    } else {
      const tx = POD_W - POD_AV_X - POD_AVATAR - 12;
      pod.addChild(this.rightTextAt(d.name, tx, 10, 20, COLORS.text, '700'));
      pod.addChild(this.rightTextAt(moneyFa(d.balance), tx, 36, 16, COLORS.gold));
      pod.addChild(this.timeChip(d.timeBank, tx, 58, 'opp'));
    }
    return pod;
  }

  /** The "reserve time" (time-bank) chip: a clear glossy pill with a clock icon + seconds. */
  private timeChip(seconds: number, anchorX: number, y: number, side: 'me' | 'opp'): Container {
    const c = new Container();
    const padX = 10;
    const h = 30;
    const iconSize = 22;
    const icon = iconSprite(this.ctx.iconTexture('clock'), iconSize);
    const num = makeText(`${toPersianDigits(seconds)}″`, { size: 19, weight: '700', fill: COLORS.text, align: 'left' });
    num.anchor.set(0, 0.5);
    const chipW = padX + iconSize + 6 + Math.ceil(num.width) + padX;
    const bg = new Graphics()
      .roundRect(0, 0, chipW, h, h / 2)
      .fill({ color: COLORS.darkPill, alpha: 0.85 })
      .stroke({ width: BORDER.thin, color: COLORS.panelBorder, alpha: 0.6 });
    icon.position.set(padX, (h - iconSize) / 2);
    num.position.set(padX + iconSize + 6, h / 2);
    c.addChild(bg, icon, num);
    c.position.set(side === 'me' ? anchorX : anchorX - chipW, y);
    return c;
  }

  /**
   * The active-turn timer. A green ring depletes the per-turn time; when it hits zero the player is on
   * the clock with their reserve (time bank), so it hands off to a RED ring depleting the reserve, with
   * the countdown node turning red and pulsing once per second (the "running out of time" VFX). The
   * countdown is a solid node sitting on the bottom of the ring. Cosmetic — the server times out.
   */
  private turnTimer(cx: number, turnSeconds: number, reserveSeconds: number): TweenHandle | null {
    if (turnSeconds <= 0 && reserveSeconds <= 0) return null;

    const nr = 19;
    const node = new Container();
    const bg = new Graphics();
    const t = makeText('', { size: 20, weight: '700', align: 'center', fill: COLORS.white });
    t.anchor.set(0.5);
    node.addChild(bg, t);
    node.position.set(cx, AV_CY + TURN_RING_R);
    this.board.addChild(node);

    const paintNode = (color: number): void => {
      bg.clear();
      bg.circle(0, 0, nr + 1)
        .fill({ color: COLORS.scrim, alpha: 0.5 })
        .circle(0, 0, nr)
        .fill({ color: COLORS.darkPill, alpha: 1 })
        .stroke({ width: BORDER.thick, color });
    };

    let active: TweenHandle | null = null;
    let lastSec = -1;

    // Phase 2: reserve (time bank) — red ring + red node pulsing each second.
    const startReserve = (): void => {
      paintNode(COLORS.danger);
      t.style.fill = COLORS.danger;
      lastSec = -1;
      pulse(node, 1.3, 320); // a pop at the moment the reserve kicks in
      active = turnRing(this.board, {
        cx,
        cy: AV_CY,
        radius: TURN_RING_R,
        seconds: reserveSeconds,
        color: COLORS.danger,
        width: 7,
        onTick: (s) => {
          if (t.destroyed) return;
          t.text = toPersianDigits(s);
          if (s !== lastSec) {
            lastSec = s;
            pulse(node, 1.14, 280);
          }
        },
      });
      this.board.addChild(node); // keep the number above the freshly-drawn ring
    };

    if (turnSeconds > 0) {
      paintNode(WIN_GREEN);
      active = turnRing(this.board, {
        cx,
        cy: AV_CY,
        radius: TURN_RING_R,
        seconds: turnSeconds,
        color: WIN_GREEN,
        width: 7,
        onTick: (s) => {
          if (!t.destroyed) t.text = toPersianDigits(s);
        },
        onComplete: () => {
          active?.cancel(); // clear the spent green ring before the red one
          active = null;
          if (reserveSeconds > 0) startReserve();
        },
      });
      this.board.addChild(node); // keep the number above the ring
    } else {
      startReserve();
    }

    return {
      cancel() {
        active?.cancel();
        if (!node.destroyed) node.destroy();
      },
    };
  }

  private handLayer(codes: string[], y: number, prevLen: number, cardW: number): void {
    if (!codes.length) return;
    const row = handFan(this.cards, codes, {
      cardWidth: cardW,
      spread: 0.16,
      shadow: true,
      maxWidth: BLACKJACK_HAND_MAX_WIDTH,
    });
    const w = row.getLocalBounds().width;

    // Soft card-zone mat behind the hand so cards read off the busy felt.
    const matW = Math.max(cardW * 2.2, w + 44);
    const matH = cardW * CARD_ASPECT + 34;
    const mat = new Graphics()
      .roundRect(DESIGN_W / 2 - matW / 2, y - 18, matW, matH, 20)
      .fill({ color: COLORS.scrim, alpha: 0.2 })
      .stroke({ width: BORDER.thin, color: COLORS.panelBorder, alpha: 0.35 });
    this.board.addChild(mat);

    row.position.set(DESIGN_W / 2, y);
    this.board.addChild(row);

    // Animate only newly dealt cards: grow → animate the appended tail; shrink/new round → animate all.
    const start = codes.length > prevLen ? prevLen : codes.length < prevLen ? 0 : codes.length;
    row.children.forEach((card, i) => {
      if (i >= start) scaleIn(card as Container, 280, 0.3);
    });
  }

  private betBox(bet: number): Container {
    const c = new Container();
    const w = 230;
    const h = 60;
    const x = (DESIGN_W - w) / 2;
    const by = this.ly.betBoxY;
    const g = new Graphics().roundRect(x, by - h / 2, w, h, 14).fill({ color: COLORS.darkPill, alpha: 0.9 }).stroke({ width: BORDER.base, color: COLORS.panelBorder });
    c.addChild(g);
    const label = makeText(`بت: ${moneyFa(bet)}`, { size: 26, weight: '700', align: 'center', fill: COLORS.white });
    label.anchor.set(0.5);
    label.position.set(DESIGN_W / 2 - 18, by);
    c.addChild(label);
    if (this.table.chips) {
      const chips = iconSprite(this.table.chips, 42);
      chips.position.set(x + w - 52, by - 21);
      c.addChild(chips);
    }
    return c;
  }

  private valueChip(value: number): Container {
    const c = new Container();
    const bust = value > 21;
    const blackjack = value === 21;
    const color = bust ? COLORS.danger : blackjack ? WIN_GREEN : COLORS.gold;
    const w = 110;
    const h = 44;
    const y = this.ly.myHandY - BLACKJACK_VALUE_CHIP_OFFSET;
    const g = new Graphics().roundRect(-w / 2, 0, w, h, 12).fill({ color: COLORS.darkPill, alpha: 0.92 }).stroke({ width: BORDER.thick, color });
    const t = makeText(bust ? `${toPersianDigits(value)} ✗` : toPersianDigits(value), { size: 24, weight: '700', align: 'center', fill: COLORS.white });
    t.anchor.set(0.5);
    t.position.set(0, h / 2);
    c.addChild(g, t);
    c.position.set(DESIGN_W / 2, y);
    if (bust && value !== this.prevValue) shake(c);
    else if (blackjack && value !== this.prevValue) pulse(c);
    return c;
  }

  private actionTray(g: GetGamePayload): Container {
    const c = new Container();
    c.position.set(ACTION_TRAY.x, this.ly.trayY);
    c.addChild(trayBackground(ACTION_TRAY.w, ACTION_TRAY.h, 'bottom'));

    const title = makeText('انتخاب حرکت', { size: 24, weight: '700', align: 'center', fill: TRAY_TITLE });
    title.anchor.set(0.5, 0);
    title.position.set(ACTION_TRAY.w / 2, 14);
    c.addChild(title);

    const items: Array<{ label: string; icon?: Texture; onTap: () => void }> = [];
    if (g.actions.includes(BlackjackActions.Hit)) items.push({ label: 'کارت بده', icon: this.table.hand, onTap: () => this.send(encodeHit(g.myHandValue)) });
    if (g.actions.includes(BlackjackActions.Stand)) items.push({ label: 'برو برا خودت', icon: this.table.stop, onTap: () => this.send(encodeStand()) });
    if (g.actions.includes(BlackjackActions.Bet)) items.push({ label: 'افزایش', icon: this.table.reach, onTap: () => this.openBet(g) });

    const colW = 170;
    const totalW = items.length * colW;
    let x = (ACTION_TRAY.w - totalW) / 2;
    for (const it of items) {
      const col = this.actionButton(it.label, it.icon, it.onTap, colW);
      col.position.set(x, 52);
      c.addChild(col);
      x += colW;
    }
    return c;
  }

  /** One action: a 64px icon over a dark pill label, the whole column tappable. */
  private actionButton(label: string, icon: Texture | undefined, onTap: () => void, colW: number): Container {
    const c = new Container();
    if (icon) {
      const ic = iconSprite(icon, 60);
      ic.position.set((colW - 60) / 2, 0);
      c.addChild(ic);
    }
    const pillW = colW - 24;
    const pillH = 40;
    const pill = new Graphics().roundRect((colW - pillW) / 2, 70, pillW, pillH, 12).fill({ color: COLORS.darkPill }).stroke({ width: BORDER.base, color: COLORS.gold });
    c.addChild(pill);
    const t = makeText(label, { size: 20, weight: '700', align: 'center', fill: COLORS.white });
    t.anchor.set(0.5);
    t.position.set(colW / 2, 70 + pillH / 2);
    c.addChild(t);

    c.hitArea = new Rectangle(0, 0, colW, 112);
    c.eventMode = 'static';
    c.cursor = 'pointer';
    c.on('pointertap', onTap);
    c.on('pointerover', () => (c.alpha = 0.85));
    c.on('pointerout', () => (c.alpha = 1));
    return c;
  }

  // ---- small text helpers ----

  private centered(text: string, y: number, opts: { size?: number; weight?: '400' | '700'; fill?: number } = {}): Container {
    const t = makeText(text, { size: opts.size ?? 24, weight: opts.weight ?? '400', align: 'center', fill: opts.fill ?? COLORS.text });
    t.anchor.set(0.5);
    t.position.set(DESIGN_W / 2, y);
    const c = new Container();
    c.addChild(t);
    return c;
  }

  private leftText(text: string, x: number, y: number, size: number, fill: number, weight: '400' | '700' = '400'): Text {
    const t = makeText(text, { size, weight, fill, align: 'left' });
    t.anchor.set(0, 0);
    t.position.set(x, y);
    return t;
  }

  private rightTextAt(text: string, x: number, y: number, size: number, fill: number, weight: '400' | '700' = '400'): Text {
    const t = makeText(text, { size, weight, fill });
    t.anchor.set(1, 0);
    t.position.set(x, y);
    return t;
  }

  // ---- transient overlays ----

  private banner(text: string, color: number, durationMs = 3000): void {
    const c = new Container();
    const w = DESIGN_W - 120;
    const h = 60;
    const panel = new Panel(w, h);
    panel.position.set((DESIGN_W - w) / 2, 470);
    const t = makeText(text, { size: 24, align: 'center', fill: color });
    t.anchor.set(0.5);
    t.position.set(w / 2, h / 2);
    panel.addChild(t);
    c.addChild(panel);
    this.overlay.addChild(c);
    scaleIn(panel, 200, 0.85);
    this.after(durationMs, () => {
      if (!c.destroyed) c.destroy({ children: true });
    });
  }

  private clearOverlay(): void {
    for (const child of this.overlay.removeChildren()) child.destroy({ children: true });
  }

  private after(ms: number, fn: () => void): void {
    const id = window.setTimeout(fn, ms);
    this.track(() => window.clearTimeout(id));
  }
}
